import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

/**
 * Voix préférée du commerce (réglage Paramètres → Assistant).
 * Chargée depuis `company_profile.tts_voice` et mise en cache par marchand
 * pour éviter une requête sur chaque message du chat.
 */
const cache = new Map<number, string>();

export function useMerchantVoice(): string {
  const { user } = useAuth();
  const merchantId = user?.id ?? 0;
  const [voice, setVoice] = useState(() => (merchantId ? cache.get(merchantId) || '' : ''));

  useEffect(() => {
    if (!merchantId) {
      setVoice('');
      return;
    }
    if (cache.has(merchantId)) {
      setVoice(cache.get(merchantId) || '');
      return;
    }
    let cancelled = false;
    supabase
      .from('company_profile')
      .select('tts_voice')
      .eq('merchant_id', merchantId)
      .maybeSingle()
      .then(({ data }) => {
        const next = String((data as { tts_voice?: string | null } | null)?.tts_voice || '');
        cache.set(merchantId, next);
        if (!cancelled) setVoice(next);
      })
      .catch(() => {
        cache.set(merchantId, '');
        if (!cancelled) setVoice('');
      });
    return () => { cancelled = true; };
  }, [merchantId]);

  return voice;
}

/** Met à jour le cache après un changement dans les paramètres. */
export function primeMerchantVoice(merchantId: number, voice: string): void {
  if (!merchantId) return;
  cache.set(merchantId, voice || '');
}

export default useMerchantVoice;